import { useState } from 'react'
import { useLocalDebouncedFn } from 'hooks/useLocalDebouncedFn'
import cx from 'classnames'

import Styles from './ValueCollection.module.scss'

import { usePropertyForCondition } from '../../../../hooks/usePropertyForCondition'
import { PropertyType } from 'api/clients/PropertyClient'
import { Condition } from '../../../../Filter'
import { Operator } from '../../../../constants'
import { Person } from 'common/Person/Person'
import { HTMLInputEvent } from 'types'
import { Date } from './components/Date/Date'
import { Checkbox } from './components/Checkbox/Checkbox'
import { OptionSelect, OptionSelectProps, OptionSelectVariant } from 'common/OptionSelect/OptionSelect'
import { useHandbookReadOnlyUser } from 'hooks/useHandbookReadOnlyUser'
import { useFinderContext } from 'screens/Finder/FinderContext'

interface Props {
  condition: Condition
  updateValue: (value: any) => void
  onChangeOperator: (operator: Operator) => void
}

export const ValueCollection = ({ condition, updateValue, onChangeOperator }: Props) => {
  const [isEditing, setIsEditing] = useState(false)
  const [inputValue, setInputValue] = useState(condition.value || '')
  const property = usePropertyForCondition(condition)
  const debouncedUpdate = useLocalDebouncedFn((text: string) => updateValue(text), 500, {}, [updateValue])
  const { isReadOnlyUser } = useHandbookReadOnlyUser()
  const { filesView } = useFinderContext()
  const isCalendarProperty = !!(
    filesView?.schema.calendar_property_id &&
    property &&
    filesView.schema.calendar_property_id === property.id
  )

  const onInputChange = (e: HTMLInputEvent) => {
    setInputValue(e.target.value)
    debouncedUpdate(e.target.value)
  }

  if (!property) return <div className={Styles.pad}></div>

  if ([Operator.Empty, Operator.NotEmpty].includes(condition.operator) && property.type !== PropertyType.Checkbox) {
    return <div className={Styles.pad}></div>
  }

  switch (property.type) {
    case PropertyType.Title:
    case PropertyType.Text:
      return (
        <input
          className={cx(Styles.input, { [Styles.isEditing]: isEditing, [Styles.uneditable]: isReadOnlyUser })}
          onBlur={() => setIsEditing(false)}
          onChange={onInputChange}
          onFocus={() => setIsEditing(true)}
          placeholder="Enter value"
          value={inputValue}
          disabled={isReadOnlyUser}
        />
      )
    case PropertyType.Person:
      return <Person value={condition.value} onUpdate={updateValue} variant="filter" readOnly={isReadOnlyUser} />
    case PropertyType.SingleSelect:
    case PropertyType.MultiSelect:
      const isMulti =
        property.type === PropertyType.MultiSelect ||
        [Operator.ContainsAny, Operator.NotContains].includes(condition.operator)
      let props: OptionSelectProps
      if (isMulti) {
        props = {
          mode: 'multiple',
          selected: property.property_values
            .filter((pv) => {
              if (Array.isArray(condition.value)) {
                return condition.value.includes(pv.id)
              } else {
                return Number(condition.value) === Number(pv.id)
              }
            })
            .map((pv) => pv.value),
          onChange: (newSelection) =>
            updateValue(
              property && property.property_values.filter((pv) => newSelection.includes(pv.value)).map((pv) => pv.id),
            ),
          availableOptions: property.property_values,
          allowEmpty: false,
          isEditable: !isReadOnlyUser,
        }
      } else {
        props = {
          mode: 'single',
          selected: property.property_values.find((pv) => condition.value === pv.id)?.value,
          onChange: (newSelection) => {
            const filteredProperties = property
              ? property.property_values.filter((pv) => newSelection === pv.value)
              : []
            updateValue(filteredProperties.length > 0 ? filteredProperties[0].id : '')
          },
          availableOptions: property.property_values,
          allowEmpty: false,
          isEditable: !isReadOnlyUser,
        }
      }
      return <OptionSelect canEditOptions={false} variant={OptionSelectVariant.Filter} {...props} />
    case PropertyType.Date:
      return <Date condition={condition} onChange={updateValue} isDisabled={isReadOnlyUser || isCalendarProperty} />
    case PropertyType.Checkbox:
      return (
        <Checkbox
          aria-label="isSelected"
          onChangeOperator={onChangeOperator}
          checked={condition.operator === Operator.NotEmpty}
          isDisabled={isReadOnlyUser}
        />
      )
    case PropertyType.Number:
      return (
        <input
          className={cx(Styles.input, { [Styles.isEditing]: isEditing, [Styles.uneditable]: isReadOnlyUser })}
          onBlur={() => setIsEditing(false)}
          onChange={onInputChange}
          onFocus={() => setIsEditing(true)}
          placeholder="Enter value"
          type="number"
          value={inputValue}
          disabled={isReadOnlyUser}
        />
      )
    case PropertyType.Link:
      return (
        <input
          className={cx(Styles.input, { [Styles.isEditing]: isEditing, [Styles.uneditable]: isReadOnlyUser })}
          value={inputValue}
          onChange={onInputChange}
          disabled={isReadOnlyUser}
        />
      )
  }

  return <div className={Styles.pad}></div>
}
